import {useState, useEffect} from "react";
import {useSelector} from "react-redux";
import {useNavigate} from "react-router-dom";
import {useTranslation} from "react-i18next";

import moment from "moment";

import Page from "@/components/Page";
import EventsCalendarPage from "@/pages/EventsCalendar";
import Loading from "@/components/Loading";
import NavigationBox from "@/components/NavigationBox";
import EmptyData from "@/components/EmptyData";
import VLFCard from "@/components/Card";
import SwitchView from "@/components/SwitchView";
import CardsList from "@/components/CardsList";
import QuickQuestions from "@/components/QuickQuestions";

import {MatchAPI} from "@/api/match";
import {PlayerAPI} from "@/api/player";
import {TrainingAPI} from "@/api/training";

import {useCategories, useReports} from "@/utils/hooks";
import {ERROR_OPERATION} from "@/utils/constants";

import "./index.scss";

const today = moment().startOf("day");

function HomePage(props) {
	const {handleNotification} = props;
	const navigate = useNavigate();
	const {t} = useTranslation();

	const seasonId = useSelector((state) => state?.season?.id);
	const playerId = useSelector((state) => state?.user?.playerId);
	const userRole = useSelector((state) => state?.user?.roleName);
	const selectedMenuItem = useSelector((state) => state?.selectedMenuItem);

	const [dashboardData, setDashboardData] = useState(false);
	const [playerData, setPlayerData] = useState();

	const [currentSwitchView, setCurrentSwitchView] = useState(
		userRole !== "Scout" ? "today" : "recap",
	);
	const {categories} = useCategories({setLoading: false, handleNotification});
	const {clubs} = []; // useClubs({setLoading: false, handleNotification});
	const {reports} = useReports({setLoading: false, handleNotification});
	const {users} = []; // useUsers({setLoading: false, handleNotification});
	const [matches, setMatches] = useState([]);
	const [trainings, setTrainings] = useState([]);

	// useEffect(() => {
	// 	if (selectedMenuItem === "settings") {
	// 		setCurrentSwitchView("recap");
	// 	}
	// }, [selectedMenuItem]);

	const [loadingData, setLoadingData] = useState(false);
	const getData = async (matchesFilters, trainingsFilters) => {
		const promises = [
			MatchAPI.list({...matchesFilters, matchObserved: false, seasonId}),
			TrainingAPI.list(trainingsFilters),
		];
		setLoadingData(true);
		try {
			const responses = await Promise.all(promises);
			setMatches(responses[0]);
			setTrainings(responses[1]);
		} catch (error) {
			console.log(error);
		} finally {
			setLoadingData(false);
		}
	};

	const [nextTrainings, setNextTrainings] = useState([]);
	const [nextMatches, setNextMatches] = useState([]);
	const [lastMatchesResults, setLastMatchesResults] = useState([]);

	const managerData = trainings && matches;
	const scoutData = reports;
	const settingsData = categories && clubs && users;

	const filterByCategory = (items, categoryKey, filterCallback) => {
		const categoriesKeys = items
			.sort((a, b) => {
				const categoryA = categories?.filter(
					(c) => c.id === a.playersCategoryId,
				)?.[0];
				const categoryB = categories?.filter(
					(c) => c.id === b.playersCategoryId,
				)?.[0];

				return categoryA.sorting - categoryB.sorting;
			})
			.reduce((acc, item) => {
				const categoryId = item[categoryKey];
				if (!acc[categoryId]) acc[categoryId] = [];
				acc[categoryId].push(item);
				return acc;
			}, {});

		return Object.keys(categoriesKeys).map((categoryId) => ({
			[categoryKey]: categoryId,
			items: categoriesKeys[categoryId].filter(filterCallback),
		}));
	};

	const filterMatchesOrTrainings = (items, dateKey, userCategoryId) => {
		const isSameOrAfter = (item) =>
			moment(item[dateKey]).isSameOrAfter(today, "day");

		return items.filter((item) =>
			userCategoryId
				? item.playersCategoryId === userCategoryId && isSameOrAfter(item)
				: isSameOrAfter(item),
		);
	};

	const matchesFilter = () => {
		if (userRole === "Player") {
			if (playerData) {
				return filterMatchesOrTrainings(
					matches,
					"startDate",
					playerData?.playerCategoryId,
				);
			}
		}

		if (["Admin", "Coach", "Analyst"].includes(userRole)) {
			return filterByCategory(matches, "playersCategoryId", (match) =>
				moment(match.startDate).startOf("day").isSameOrAfter(today),
			);
		}

		return matches?.filter((el) =>
			moment(el.startDate).isSameOrAfter(today, "day"),
		);
	};

	const matchesResultsFilter = () => {
		const isEndDateBeforeOrEqualToday = (match) =>
			moment(match.endDate).isSameOrBefore(today, "hour");

		if (userRole === "Player") {
			getPlayer();
			return matches
				?.filter(
					(el) =>
						el.playersCategoryId === playerData?.playerCategoryId &&
						isEndDateBeforeOrEqualToday(el),
				)
				.reverse();
		}

		if (
			userRole === "Admin" ||
			userRole === "Coach" ||
			userRole === "Analyst"
		) {
			return filterByCategory(
				matches,
				"playersCategoryId",
				isEndDateBeforeOrEqualToday,
			);
		}

		return matches?.filter(isEndDateBeforeOrEqualToday);
	};

	const trainingsFilter = () => {
		const isSameOrAfter = (training) =>
			moment(training.startDate).startOf("day").isSameOrAfter(today);

		if (userRole === "Player") {
			getPlayer();
			return filterMatchesOrTrainings(
				trainings,
				"startDate",
				playerData?.playerCategoryId,
			);
		}

		if (["Admin", "Coach"].includes(userRole)) {
			return filterByCategory(trainings, "playersCategoryId", isSameOrAfter);
		}

		return trainings?.filter(isSameOrAfter);
	};

	const getPlayer = async () => {
		try {
			const response = await PlayerAPI.getById(playerId, {seasonId});
			response.hasStats =
				parseInt(response.playerCategoryName.split(" ")?.[1]) > 13;
			setPlayerData(response);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		}
	};

	useEffect(() => {
		if (userRole === "Player") {
			getPlayer();
		}
	}, []);

	useEffect(() => {
		if (userRole !== "Player") {
			setNextMatches(matchesFilter);
			setNextTrainings(trainingsFilter);
			setLastMatchesResults(matchesResultsFilter);
		}
	}, [trainings, matches, userRole]);

	useEffect(() => {
		if (playerData && trainings?.length && matches?.length && !dashboardData) {
			setNextMatches(matchesFilter);
			setNextTrainings(trainingsFilter);
			setLastMatchesResults(matchesResultsFilter);
			setDashboardData(true);
		}
	}, [trainings, matches, playerData]);

	const loading =
		(userRole === "Scout" && !scoutData) || !managerData || loadingData;

	const managerCoachNextMatchesEmpty = !nextMatches.filter(
		(nm) => nm.items?.length > 0,
	)?.length;
	const managerCoachNextTrainingsEmpty = !nextTrainings.filter(
		(nt) => nt.items?.length > 0,
	)?.length;
	const managerCoachLastMatchesEmpty = !lastMatchesResults.filter(
		(lm) => lm.items?.length > 0,
	)?.length;

	const [nextMatchesOpened, setNextMatchesOpened] = useState(true);
	const [matchesOpened, setMatchesOpened] = useState(true);
	const [nextTrainingsOpened, setNextTrainingsOpened] = useState(true);

	const getNextMatchesRender = (matches) => {
		return (
			<>
				{matches.map((el) => {
					const currentPosition = 0;
					const sortedItems = el.items?.sort((a, b) =>
						a["startDate"] > b["startDate"]
							? 1
							: b["startDate"] > a["startDate"]
								? -1
								: 0,
					);
					return sortedItems?.[currentPosition] ? (
						<NavigationBox
							key={el.items[currentPosition]?.id}
							dashboard
							label={
								el.items[currentPosition]?.homeClub +
								" - " +
								el.items[currentPosition]?.awayClub
							}
							value={moment(el.items[currentPosition]?.startDate).format(
								"DD/MM/YYYY HH:mm",
							)}
							info={el.items[currentPosition]?.playersCategory}
							dateInfo={moment(el.items[currentPosition]?.startDate).isSame(
								moment(),
								"day",
							)}
							imagePath={el.items[currentPosition]?.matchType}
							onClick={() =>
								navigate("/matches/" + el.items[currentPosition]?.id)
							}
						/>
					) : null;
				})}
			</>
		);
	};
	const getLastMatchesRender = (matches) => {
		return (
			<>
				{matches.map((el) => {
					const currentPosition = 0;
					const sortedItems = el.items?.sort((a, b) =>
						b["startDate"] > a["startDate"]
							? 1
							: a["startDate"] > b["startDate"]
								? -1
								: 0,
					);
					return sortedItems?.[currentPosition] ? (
						<NavigationBox
							key={el.items[currentPosition].id}
							dashboard
							label={
								el.items[currentPosition]?.homeClub +
								" - " +
								el.items[currentPosition]?.awayClub
							}
							score={el.items[currentPosition]?.composedResult || "---"}
							value={moment(el.items[currentPosition]?.startDate).format(
								"DD/MM/YYYY HH:mm",
							)}
							info={el.items[currentPosition]?.playersCategory}
							dateInfo={moment(el.items[currentPosition]?.startDate).isSame(
								moment(),
								"day",
							)}
							imagePath={el.items[currentPosition]?.matchType}
							onClick={() =>
								navigate("/matches/" + el.items[currentPosition]?.id)
							}
						/>
					) : null;
				})}
			</>
		);
	};
	const getNextTrainings = (trainings, firstElement = true) => {
		return (
			<>
				{trainings.map((el) => {
					const currentPosition = firstElement ? 0 : el.items.length - 1;
					return el.items?.[currentPosition] ? (
						<div
							key={el.items[currentPosition]?.id}
							className="col-6 col-md-4 col-lg-3 no-padding"
						>
							<NavigationBox
								dashboard
								label={moment(el.items[currentPosition]?.startDate).format(
									"DD/MM/YYYY HH:mm",
								)}
								value={el.items[currentPosition]?.fieldName}
								info={el.items[currentPosition]?.playersCategory}
								dateInfo={moment(el.items[currentPosition]?.startDate).isSame(
									moment(),
									"day",
								)}
								onClick={() =>
									navigate("/trainings/" + el.items[currentPosition]?.id)
								}
							/>
						</div>
					) : null;
				})}
			</>
		);
	};

	useEffect(() => {
		let matchesFilters = {};
		let trainingsFilters = {};
		if (currentSwitchView === "today") {
			matchesFilters = {
				fromDate: moment().format("YYYY-MM-DD"),
				toDate: moment().add(1, "days").format("YYYY-MM-DD"),
			};
			trainingsFilters = {
				fromDate: moment().format("YYYY-MM-DD"),
				toDate: moment().add(1, "days").format("YYYY-MM-DD"),
			};
		} else {
			matchesFilters = {
				fromDate: moment().subtract(1, "months").format("YYYY-MM-DD"),
				toDate: moment().add(1, "months").format("YYYY-MM-DD"),
			};
			trainingsFilters = {
				fromDate: moment().subtract(1, "days").format("YYYY-MM-DD"),
				toDate: moment().add(1, "days").format("YYYY-MM-DD"),
			};
		}
		getData(matchesFilters, trainingsFilters);
	}, [currentSwitchView]);

	let boxes = <></>;
	switch (selectedMenuItem) {
		case "manager":
		case "coach":
		case "settings":
			boxes = (
				<div className="row no-margin col-container h-100">
					<div className="col-12 col-lg-6 padding-small">
						{nextMatches && managerData ? (
							<div
								className={
									"homepage-block" + (nextMatchesOpened ? " opened" : "closed")
								}
							>
								<VLFCard
									title="Prossime partite"
									content={
										<div className="home-card-container">
											{getNextMatchesRender(nextMatches)}
											{managerCoachNextMatchesEmpty && <EmptyData />}
										</div>
									}
									openable={false}
									opened={nextMatchesOpened}
									setOpened={setNextMatchesOpened}
								/>
							</div>
						) : null}
					</div>
					<div className="col-12 col-lg-6 padding-small">
						{lastMatchesResults && managerData ? (
							<div
								className={
									"homepage-block" + (matchesOpened ? " opened" : "closed")
								}
							>
								<VLFCard
									title="Ultimi risultati"
									content={
										<div className="home-card-container">
											{getLastMatchesRender(lastMatchesResults)}
											{managerCoachLastMatchesEmpty && <EmptyData />}
										</div>
									}
									openable={false}
									opened={matchesOpened}
									setOpened={setMatchesOpened}
								/>
							</div>
						) : null}
					</div>
					<div className="col-12 padding-small mt-2">
						{nextTrainings && managerData ? (
							<div
								className={
									"homepage-block" +
									(nextTrainingsOpened ? " opened" : "closed")
								}
							>
								<VLFCard
									title="Prossimi allenamenti"
									content={
										<div className="home-card-container small">
											<div className="row no-margin">
												{getNextTrainings(nextTrainings)}
												{managerCoachNextTrainingsEmpty && <EmptyData />}
											</div>
										</div>
									}
									openable={false}
									opened={nextTrainingsOpened}
									setOpened={setNextTrainingsOpened}
								/>
							</div>
						) : null}
					</div>
				</div>
			);
			break;
		case "player":
			boxes = (
				<div className="row no-margin col-container h-100">
					<div className="col-12 col-lg-6 padding-small">
						{nextMatches && managerData ? (
							<div
								className={
									"homepage-block" + (nextMatchesOpened ? " opened" : "closed")
								}
							>
								<VLFCard
									title="Prossime partite"
									content={
										<div className="home-card-container">
											{nextMatches
												.sort((a, b) =>
													a["startDate"] > b["startDate"]
														? 1
														: b["startDate"] > a["startDate"]
															? -1
															: 0,
												)
												.map((el) => (
													<NavigationBox
														key={el?.id}
														dashboard
														label={el?.homeClub + " - " + el?.awayClub}
														value={moment(el?.startDate).format(
															"DD/MM/YYYY HH:mm",
														)}
														info={el?.playersCategory}
														dateInfo={moment(el?.startDate).isSame(
															moment(),
															"day",
														)}
														imagePath={el?.matchType}
														onClick={() => navigate("/matches/" + el?.id)}
													/>
												))}
											{!nextMatches?.length && <EmptyData />}
										</div>
									}
									openable={false}
									opened={nextMatchesOpened}
									setOpened={setNextMatchesOpened}
								/>
							</div>
						) : null}
					</div>
					<div className="col-12 col-lg-6 padding-small">
						{lastMatchesResults && managerData ? (
							<div
								className={
									"homepage-block" + (matchesOpened ? " opened" : "closed")
								}
							>
								<VLFCard
									title="Ultimi risultati"
									content={
										<div className="home-card-container">
											{lastMatchesResults
												.sort((a, b) =>
													b["startDate"] > a["startDate"]
														? 1
														: a["startDate"] > b["startDate"]
															? -1
															: 0,
												)
												.map((el) => (
													<NavigationBox
														key={el.id}
														dashboard
														label={el?.homeClub + " - " + el?.awayClub}
														score={el?.results || "---"}
														value={moment(el?.startDate).format(
															"DD/MM/YYYY HH:mm",
														)}
														info={el?.playersCategory}
														dateInfo={moment(el?.startDate).isSame(
															moment(),
															"day",
														)}
														imagePath={el?.matchType}
														onClick={() => navigate("/matches/" + el?.id)}
													/>
												))}
											{!lastMatchesResults?.length && <EmptyData />}
										</div>
									}
									openable={false}
									opened={matchesOpened}
									setOpened={setMatchesOpened}
								/>
							</div>
						) : null}
					</div>
					<div className="col-12 padding-small mt-2">
						{nextTrainings && managerData ? (
							<div
								className={
									"homepage-block" +
									(nextTrainingsOpened ? " opened" : "closed")
								}
							>
								<VLFCard
									title="Prossimi allenamenti"
									content={
										<div className="home-card-container small">
											<div className="row no-margin">
												{nextTrainings.map((el) => (
													<div
														key={el?.id}
														className="col-6 col-md-4 col-lg-3 no-padding"
													>
														<NavigationBox
															dashboard
															label={moment(el?.startDate).format(
																"DD/MM/YYYY HH:mm",
															)}
															value={el?.fieldName}
															info={el?.playersCategory}
															dateInfo={moment(el?.startDate).isSame(
																moment(),
																"day",
															)}
															onClick={() => navigate("/trainings/" + el?.id)}
														/>
													</div>
												))}
												{!nextTrainings?.length && <EmptyData />}
											</div>
										</div>
									}
									openable={false}
									opened={nextTrainingsOpened}
									setOpened={setNextTrainingsOpened}
								/>
							</div>
						) : null}
					</div>
				</div>
			);
			break;
		case "analyst":
			boxes = (
				<div className="row no-margin col-container h-100">
					<div className="col-12 col-lg-6 padding-small">
						{nextMatches && managerData ? (
							<div
								className={
									"homepage-block" + (nextMatchesOpened ? " opened" : "closed")
								}
							>
								<VLFCard
									title="Prossime partite"
									content={
										<div className="home-card-container">
											{getNextMatchesRender(nextMatches)}
											{!nextMatches?.length && <EmptyData />}
										</div>
									}
									openable={false}
									opened={nextMatchesOpened}
									setOpened={setNextMatchesOpened}
								/>
							</div>
						) : null}
					</div>
					<div className="col-12 col-lg-6 padding-small">
						{lastMatchesResults && managerData ? (
							<div
								className={
									"homepage-block" + (matchesOpened ? " opened" : "closed")
								}
							>
								<VLFCard
									title="Ultimi risultati"
									content={
										<div className="home-card-container">
											{getLastMatchesRender(lastMatchesResults)}
											{!lastMatchesResults?.length && <EmptyData />}
										</div>
									}
									openable={false}
									opened={matchesOpened}
									setOpened={setMatchesOpened}
								/>
							</div>
						) : null}
					</div>
				</div>
			);
			break;
		case "scout":
			boxes = (
				<div className="row no-margin col-container h-100">
					<div className="col-12 padding-small">
						{scoutData && (
							<VLFCard
								title="Ultimi 10 report inseriti"
								content={
									<div className="row no-margin">
										{reports.length > 0 ? (
											reports
												.sort(
													(a, b) =>
														new Date(b.reportDate) - new Date(a.reportDate),
												)
												.slice(0, 10)
												.map((report) => (
													<div
														key={report.id}
														className="col-12 col-md-6 no-padding"
													>
														<NavigationBox
															dashboard
															label={
																report.player?.firstName +
																" " +
																report.player?.lastName
															}
															value={
																report.club?.name +
																(report.playerCategory
																	? " (" + report.playerCategory.name + ")"
																	: "")
															}
															info={report.signalerName}
															imagePath={"Scout"}
															imageLabel={moment(report.reportDate).format(
																"DD/MM/YYYY",
															)}
															onClick={() =>
																navigate("/scouting/reports/" + report.id)
															}
														/>
													</div>
												))
										) : (
											<EmptyData />
										)}
									</div>
								}
								openable={false}
							/>
						)}
					</div>
				</div>
			);
			break;
		case "settings_old":
			boxes = (
				<div className="application-sections-boxes">
					<NavigationBox
						label="Categorie"
						value={settingsData && categories?.length}
						imagePath={require("@/assets/images/manager/category.png")}
						onClick={() => navigate("/settings/categories")}
					/>
					<NavigationBox
						label="Club"
						value={settingsData && clubs?.length}
						imagePath={require("@/assets/images/manager/club.png")}
						onClick={() => navigate("/settings/clubs")}
					/>
					<NavigationBox
						label="Utenti"
						value={settingsData && users?.length}
						imagePath={require("@/assets/images/user.png")}
						onClick={() => navigate("/settings/users")}
					/>
				</div>
			);
			break;
		default:
			break;
	}

	const matchesConfiguration = {
		data: !loadingData ? matches : [],
		onClick: (match) => navigate("/matches/" + match.id),
	};

	const trainingsConfiguration = {
		data: !loadingData ? trainings : [],
		onClick: (training) => navigate("/trainings/" + training.id),
	};

	return (
		<Page>
			<Loading visible={loading} />
			{userRole !== "Scout" && (
				<SwitchView
					currentSwitchView={currentSwitchView}
					setCurrentSwitchView={setCurrentSwitchView}
					pageOptions={[
						{value: "today", label: t("today_events")},
						{value: "calendar", label: t("calendar")},
						{value: "recap", label: t("events_diary")},
					]}
				/>
			)}
			{currentSwitchView === "today" ? (
				<div className="row no-margin homepage-container">
					<div className="col-sm-12 no-padding">
						<div className="d-flex flex-column gap-3 w-100">
							{/* {["Admin", "Coach", "Analyst"].includes(userRole) && (
								<QuickQuestions handleNotification={handleNotification} />
							)} */}
							<div className="d-flex flex-column w-100">
								<div
									className="px-1"
									style={{fontSize: "16px", fontWeight: "bold"}}
								>
									{t("matches")}
								</div>
								<CardsList
									type="match"
									listConfiguration={matchesConfiguration}
									defaultSortedField="startDate"
									defaultSortedOrder="descend"
									fitHeight
								/>
							</div>
							<div className="d-flex flex-column w-100">
								<div
									className="px-1"
									style={{fontSize: "16px", fontWeight: "bold"}}
								>
									{t("trainings")}
								</div>
								<CardsList
									type="training"
									listConfiguration={trainingsConfiguration}
									defaultSortedField="playersCategory"
									defaultSortedOrder="descend"
									fitHeight
								/>
							</div>
						</div>
					</div>
				</div>
			) : currentSwitchView === "calendar" ? (
				<EventsCalendarPage inPage />
			) : (
				<div
					className={
						"row no-margin homepage-container" +
						(userRole === "Scout" ? " pt-0" : "")
					}
				>
					<div className="col-sm-12 no-padding">
						{/* {["Admin", "Coach", "Analyst"].includes(userRole) && (
							<div className="mb-3">
								<QuickQuestions handleNotification={handleNotification} />
							</div>
						)} */}
						{boxes}
					</div>
				</div>
			)}
		</Page>
	);
}

export default HomePage;
